import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import MenuIcon from '@mui/icons-material/Menu';

interface NavLink {
  label: string;
  to: string;
}

const NAV_LINKS: NavLink[] = [
  { label: 'Home', to: '/' },
  { label: 'Browse Cars', to: '/cars' },
  { label: 'Best Cars', to: '/best-cars' },
  { label: 'Reliability Data', to: '/reliability' },
  { label: 'About', to: '/about' },
];

export function AppNavbar() {
  const location = useLocation();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <AppBar
      position="sticky"
      color="primary"
      elevation={scrolled ? 4 : 0}
      sx={{ transition: (theme) => theme.transitions.create('box-shadow') }}
    >
      <Toolbar sx={{ gap: 0.5, minHeight: 64 }}>
        <Box
          component={Link}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 'auto' }}
        >
          <Box
            component="img"
            src="/golfback.jpg"
            alt="AutoAdviser logo"
            sx={{ width: 34, height: 34, mr: 1.5, borderRadius: 1.5 }}
          />
          <Typography variant="h6" component="span" color="common.white" fontWeight={800}>
            AutoAdviser
          </Typography>
        </Box>

        {/* Narrow screens: links collapse into a menu so the bar never overflows. */}
        <IconButton
          color="inherit"
          aria-label="Open navigation menu"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {NAV_LINKS.map((link) => (
            <MenuItem
              key={link.to}
              component={Link}
              to={link.to}
              selected={location.pathname === link.to}
              onClick={() => setMenuAnchor(null)}
            >
              {link.label}
            </MenuItem>
          ))}
        </Menu>

        {NAV_LINKS.map((link) => {
          const active = location.pathname === link.to;
          return (
            <Button
              key={link.to}
              component={Link}
              to={link.to}
              color="inherit"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                px: 1.5,
                opacity: active ? 1 : 0.75,
                borderBottom: '2px solid',
                borderColor: active ? 'secondary.main' : 'transparent',
                borderRadius: 0,
                '&:hover': { opacity: 1, backgroundColor: 'transparent' },
              }}
            >
              {link.label}
            </Button>
          );
        })}
      </Toolbar>
    </AppBar>
  );
}
